import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Mail, Phone, Building2, Zap, Calendar, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Link } from 'react-router-dom';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadsPage() {
  var { token } = useAuth();
  var [leads, setLeads] = useState([]);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(true);
  var [statusFilter, setStatusFilter] = useState('all');

  useEffect(function() {
    setLoading(true);
    var params = 'authorization=' + token + '&limit=100';
    if (statusFilter !== 'all') params += '&status=' + statusFilter;
    axios.get(API_URL + '/api/leads?' + params)
      .then(function(r) { setLeads(r.data.leads); setTotal(r.data.total); })
      .catch(function() { toast.error('Failed to load leads'); })
      .finally(function() { setLoading(false); });
  }, [token, statusFilter]);

  var formatDate = function(d) { return d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''; };
  var statusColors = { 'new': 'bg-blue-100 text-blue-700', 'bill_uploaded': 'bg-amber-100 text-amber-700', 'compared': 'bg-green-100 text-green-700' };

  return (
    <Layout>
      <div className="space-y-6" data-testid="leads-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Customer Leads</h1>
            <p className="text-slate-600">{total} leads from the customer portal</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="leads-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="bill_uploaded">Bill Uploaded</SelectItem>
              <SelectItem value="compared">Compared</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
        ) : leads.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500"><Users className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>No leads yet</p><p className="text-sm mt-1">Leads will appear here when customers use the portal</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {leads.map(function(lead) {
              return (
                <Card key={lead.id} className="hover:shadow-md transition-shadow" data-testid={'lead-' + lead.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">{lead.name}</span>
                          <Badge className={statusColors[lead.status] || 'bg-slate-100 text-slate-700'}>{lead.status}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          {lead.business_name && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{lead.business_name}</span>}
                          <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{lead.email}</span>
                          {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{lead.phone}</span>}
                          {lead.nmi && <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" />NMI: {lead.nmi}</span>}
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(lead.created_at)}</span>
                        </div>
                      </div>
                      {lead.comparison_id && (
                        <Link to={'/comparison/' + lead.comparison_id} className="flex items-center gap-1 text-sm text-primary hover:underline">
                          View Comparison<ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
